/**
 * Compliance Checklist Manager Component
 * Admin interface for managing compliance items (add, edit, disable)
 * With upload and paper copy on file functionality
 */
import React, { useState, useRef, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
  DialogDescription, DialogFooter
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import axios from "axios";
import {
  Plus, Edit, Trash2, Settings, Search, Filter,
  CheckCircle2, XCircle, Archive, ToggleLeft,
  Upload, Paperclip, FileText, Loader2, ChevronDown, ChevronUp, RefreshCw, AlertTriangle, RotateCcw,
  Pencil, Check, X
} from "lucide-react";

// Import the Staff Reports tab components for embedding
import { 
  LiabilityInsuranceTab, 
  OccupancyPermitTab, 
  FireDrillLogTab,
  TransportationNotificationTab,
  EmergencyDrillLogTab,
  EmergencyPlanDeliveryTab,
  EmergencyPlanProcedureTab,
  EmergencyContactListTab,
  EnrolledChildrenListTab,
  ShakenBabyPolicyTab,
  COOPTab
} from "@/components/staff-reports";

import ChildServiceReportSummaryTab from "@/components/compliance/ChildServiceReportSummaryTab";
import StaffEvaluationSummaryTab from "@/components/compliance/StaffEvaluationSummaryTab";
import EmergencyContactReportPage from "@/pages/EmergencyContactReportPage";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Inline upload drop zone component used under Paper Copy on File
const UploadDropZone = ({ itemId, onSuccess }) => {
  const [uploading, setUploading] = React.useState(false);
  const [dragOver, setDragOver] = React.useState(false);
  const [docs, setDocs] = React.useState([]);
  const ref = React.useRef(null);

  React.useEffect(() => {
    axios.get(`${API}/compliance/documents/${itemId}`)
      .then(r => setDocs(r.data?.documents || []))
      .catch(() => {});
  }, [itemId]);

  const upload = async (file) => {
    if (!file) return;
    const allowed = ['application/pdf','image/jpeg','image/png','image/jpg'];
    if (!allowed.includes(file.type)) { toast.error("Please upload a PDF or image file"); return; }
    if (file.size > 15 * 1024 * 1024) { toast.error("File size must be less than 15MB"); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await axios.post(`${API}/compliance/status/${itemId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const r = await axios.get(`${API}/compliance/documents/${itemId}`);
      setDocs(r.data?.documents || []);
      toast.success("Document uploaded");
      if (onSuccess) onSuccess();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };

  return (
    <div className="mt-3 pt-3 border-t border-gray-100">
      <div
        onDrop={(e) => { e.preventDefault(); setDragOver(false); upload(e.dataTransfer.files?.[0]); }}
        onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onClick={() => !uploading && ref.current?.click()}
        className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${
          dragOver ? 'border-green-500 bg-green-50' : 'border-gray-200 bg-gray-50 hover:border-green-400'
        }`}
      >
        {uploading
          ? <p className="text-sm text-green-600">Uploading...</p>
          : <><p className="text-sm text-gray-500">{dragOver ? 'Drop file here' : 'Click or drag & drop to upload document'}</p>
             <p className="text-xs text-gray-400 mt-0.5">PDF, JPG, PNG — Max 15MB</p></>
        }
      </div>
      <input ref={ref} type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
      {docs.map(doc => (
        <div key={doc.id} className="flex items-center gap-2 text-xs text-gray-600 bg-green-50 rounded px-2 py-1 mt-1 border border-green-200">
          <span className="truncate">{doc.fileName}</span>
          <span className="text-gray-400 ml-auto flex-shrink-0">{doc.uploadedAt ? new Date(doc.uploadedAt).toLocaleDateString() : ''}</span>
        </div>
      ))}
    </div>
  );
};

// Map item names to embedded tab components
const EMBEDDED_TAB_ITEMS = {
  "liability insurance certificate": "liability_insurance",
  "liability insurance": "liability_insurance",
  "occupancy permit": "occupancy_permit",
  "occupancy certificate": "occupancy_permit",
  "fire drill log": "fire_drill",
  "fire drill": "fire_drill",
  "fire drill record": "fire_drill",
  "annual excursion": "transportation",
  "transportation letter": "transportation",
  "annual excursion/transportation": "transportation",
  "emergency drill log": "emergency_drill",
  "emergency drill": "emergency_drill",
  "documentation of emergency plan": "emergency_plan_delivery",
  "emergency plan delivery": "emergency_plan_delivery",
  "continuity of operations": "coop_plan",
  "emergency continuity of operations plan": "coop_plan",
  "emergency plan & procedure": "emergency_plan_procedure",
  "emergency plan and procedure": "emergency_plan_procedure",
  "emergency contact list": "emergency_contact_list",
  "enrolled children list": "enrolled_children",
  "enrolled children": "enrolled_children",
  "shaken baby": "shaken_baby_policy",
  "shaken baby syndrome": "shaken_baby_policy",
  "abusive head trauma": "shaken_baby_policy",
  "child maltreatment": "shaken_baby_policy",
  "how to identify & prevent shaken baby": "shaken_baby_policy",
  "how to identify and prevent shaken baby": "shaken_baby_policy",
  "child service report": "child_service_report",
  "child service reports": "child_service_report",
  "staff evaluation": "staff_evaluation",
  "staff evaluation records": "staff_evaluation",
  "staff evaluations": "staff_evaluation",
};

// Check if item should show embedded tab instead of upload form
const getEmbeddedTabType = (itemName) => {
  const normalizedName = (itemName || "").toLowerCase().trim();
  for (const [key, tabType] of Object.entries(EMBEDDED_TAB_ITEMS)) {
    if (normalizedName.includes(key) || key.includes(normalizedName)) {
      return tabType;
    }
  }
  return null;
};

// Default category configuration
const DEFAULT_CATEGORY_CONFIG = {
  facility_documents: { 
    label: "Facility Documents", 
    color: "bg-blue-100 text-blue-700",
    icon: "FileText"
  },
  staff_records: { 
    label: "Staff Records", 
    color: "bg-purple-100 text-purple-700",
    icon: "Users"
  },
  children_records: { 
    label: "Children Records", 
    color: "bg-green-100 text-green-700",
    icon: "Baby"
  },
  emergency_preparedness: { 
    label: "Emergency Preparedness", 
    color: "bg-red-100 text-red-700",
    icon: "AlertTriangle"
  },
  health_safety: { 
    label: "Health & Safety", 
    color: "bg-yellow-100 text-yellow-700",
    icon: "Shield"
  },
  training: { 
    label: "Training & Development", 
    color: "bg-indigo-100 text-indigo-700",
    icon: "GraduationCap"
  }
};

const ComplianceChecklistManager = ({ items: propItems = [], categoryConfig = DEFAULT_CATEGORY_CONFIG, onUpdate }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterCategory, setFilterCategory] = useState("all");
  const [showDisabled, setShowDisabled] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [resetDialogOpen, setResetDialogOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [localItems, setLocalItems] = useState([]);
  const [loadingItems, setLoadingItems] = useState(false);
  const [restoringItem, setRestoringItem] = useState(null);
  const [autoInitialized, setAutoInitialized] = useState(false);
  
  // Fetch items including disabled when showDisabled changes
  const fetchItems = async (includeDisabled) => {
    setLoadingItems(true);
    try {
      // Pass include_disabled=true to backend when Show Disabled is on
      const url = includeDisabled
        ? `${API}/compliance/status?include_disabled=true`
        : `${API}/compliance/status`;
      const response = await axios.get(url);
      const fetchedItems = response.data.items || [];
      setLocalItems(fetchedItems);
      return fetchedItems;
    } catch (error) {
      console.error("Failed to fetch items:", error);
      setLocalItems(propItems);
      return propItems;
    } finally {
      setLoadingItems(false);
    }
  };
  
  // Auto-initialize compliance items if empty
  const autoInitializeItems = async () => {
    if (autoInitialized) return;
    setAutoInitialized(true);
    
    try {
      // Always check for newly-added system/default items using the existing
      // ADDITIVE endpoint. It inserts only defaults missing by exact name and
      // never changes/deletes/resets existing, custom, or disabled items.
      // Silent when nothing is missing; refetch when new items are added.
      const initResponse = await axios.post(`${API}/compliance/add-missing-items`);
      if (initResponse.data.added?.length > 0) {
        toast.success(`Added ${initResponse.data.added.length} new compliance item(s)`);
        // Refetch so the newly added items appear
        fetchItems(showDisabled);
      }
    } catch (error) {
      console.error("Auto-init error:", error);
    }
  };
  
  // Initialize with prop items and auto-initialize if needed
  useEffect(() => {
    setLocalItems(propItems);
    // Auto-initialize on mount
    autoInitializeItems();
  }, []);
  
  // Update when propItems change
  useEffect(() => {
    if (propItems.length > 0) {
      setLocalItems(propItems);
    }
  }, [propItems]);
  
  // Refetch when showDisabled changes
  useEffect(() => {
    if (showDisabled) {
      fetchItems(true);
    } else {
      // When hiding disabled, just filter locally or refetch
      fetchItems(false);
    }
  }, [showDisabled]);
  
  // Restore individual disabled item
  const handleRestoreItem = async (item) => {
    setRestoringItem(item.id);
    try {
      await axios.put(`${API}/compliance/items/${item.id}`, { isEnabled: true });
      toast.success(`"${item.name}" restored`);
      // Update local state only — don't re-fetch so remaining disabled items stay visible
      setLocalItems(prev => prev.map(i => i.id === item.id ? { ...i, isEnabled: true } : i));
      // Defer parent update until user is done restoring
      if (onUpdate) onUpdate();
    } catch (error) {
      console.error("Failed to restore item:", error);
      toast.error("Failed to restore item");
    } finally {
      setRestoringItem(null);
    }
  };

  const handleRestoreAll = async () => {
    const disabledItems = localItems.filter(i => i.isEnabled === false);
    if (disabledItems.length === 0) return;
    try {
      await Promise.all(disabledItems.map(item =>
        axios.put(`${API}/compliance/items/${item.id}`, { isEnabled: true })
      ));
      toast.success(`${disabledItems.length} item${disabledItems.length !== 1 ? 's' : ''} restored`);
      setLocalItems(prev => prev.map(i => ({ ...i, isEnabled: true })));
      setShowDisabled(false);
      if (onUpdate) onUpdate();
    } catch {
      toast.error("Failed to restore all items");
    }
  };
  
  // Use localItems for display
  const items = localItems;
  
  // Upload and paper copy state
  const [expandedItem, setExpandedItem] = useState(null);
  const [uploadingItem, setUploadingItem] = useState(null);
  const [dragOverItem, setDragOverItem] = useState(null);
  const [itemDocs, setItemDocs] = useState({}); // { [itemId]: [{id, fileName, displayName, fileType, createdAt, uploadedBy}] }
  const [loadingDocs, setLoadingDocs] = useState(null);
  const [togglingPaperCopy, setTogglingPaperCopy] = useState(null);
  const [renamingDoc, setRenamingDoc] = useState(null); // { itemId, docKey }
  const [renameDocValue, setRenameDocValue] = useState("");
  const [loadingPaperRecords, setLoadingPaperRecords] = useState(null);
  const fileInputRefs = useRef({});

  // Form state
  const [formData, setFormData] = useState({
    name: "",
    category: "facility_documents",
    description: "",
    expirationRule: "none",
    customExpirationDays: "",
    weight: 5,
    isEnabled: true,
    requiresDocument: true
  });

  // Handle file upload
  const handleFileUpload = async (itemId, file) => {
    if (!file) return;
    
    if (file.size > 15 * 1024 * 1024) {
      toast.error("File size must be less than 15MB");
      return;
    }
    
    setUploadingItem(itemId);
    
    try {
      const formData = new FormData();
      formData.append("file", file);
      
      await axios.post(`${API}/compliance/status/${itemId}/upload`, formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      
      toast.success("Document uploaded");
      // Optimistic local update. Date-driven items (Emergency Drill Log) must NOT be
      // marked complete by a bare upload — their status comes from the logged drill date.
      const _uploadedItem = localItems.find(i => i.id === itemId);
      const _isDrill = (_uploadedItem?.name || '').toLowerCase().includes('emergency drill');
      setLocalItems(prev => prev.map(item => 
        item.id === itemId 
          ? { ...item, hasUploadedDocument: true, ...(_isDrill ? {} : { complianceStatus: 'complete' }) }
          : item
      ));
      onUpdate?.();
      fetchItemDocs(itemId);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload document");
    } finally {
      setUploadingItem(null);
    }
  };

  // Fetch documents for an item
  const fetchItemDocs = async (itemId) => {
    setLoadingDocs(itemId);
    try {
      const res = await axios.get(`${API}/compliance/documents/${itemId}`);
      setItemDocs(prev => ({ ...prev, [itemId]: res.data.documents || [] }));
    } catch { /* silent */ } finally {
      setLoadingDocs(null);
    }
  };

  // Fetch paper copy records for an item
  const fetchPaperCopyRecords = async (itemId) => {
    setLoadingPaperRecords(itemId);
    try {
      const res = await axios.get(`${API}/compliance/status/${itemId}/paper-copy-records`);
      setPaperCopyRecords(prev => ({ ...prev, [itemId]: res.data.records || [] }));
    } catch { /* silent */ } finally {
      setLoadingPaperRecords(null);
    }
  };

  // View document in new tab using authenticated fetch
  const viewDocument = async (itemId, docId, fileName) => {
    try {
      const res = await axios.get(`${API}/compliance/documents/${itemId}/${docId}/download`, {
        responseType: 'blob',
      });
      const url = URL.createObjectURL(res.data);
      const win = window.open(url, '_blank', 'noopener,noreferrer');
      // Revoke after tab opens
      setTimeout(() => URL.revokeObjectURL(url), 10000);
      if (!win) toast.error("Pop-up blocked. Please allow pop-ups for this site.");
    } catch {
      toast.error("Unable to open document");
    }
  };

  // Delete document
  const deleteDocument = async (itemId, doc) => {
    // Use id if available, fallback to fileName for legacy docs
    const docKey = doc.id || doc.fileName;
    try {
      await axios.delete(`${API}/compliance/documents/${itemId}/${encodeURIComponent(docKey)}`);
      const remaining = (itemDocs[itemId] || []).filter(d => (d.id || d.fileName) !== docKey);
      setItemDocs(prev => ({ ...prev, [itemId]: remaining }));
      if (remaining.length === 0) {
        setLocalItems(prev => prev.map(item =>
          item.id === itemId
            ? { ...item, hasUploadedDocument: false, complianceStatus: item.hasPaperCopy ? 'complete' : 'missing' }
            : item
        ));
      }
      toast.success("Document deleted");
      onUpdate?.();
    } catch {
      toast.error("Failed to delete document");
    }
  };

  // Rename document
  const startRenameDoc = (itemId, doc) => {
    const docKey = doc.id || doc.fileName;
    setRenamingDoc({ itemId, docKey });
    setRenameDocValue(doc.displayName || doc.fileName || "");
  };
  const cancelRenameDoc = () => { setRenamingDoc(null); setRenameDocValue(""); };
  const saveRenameDoc = async () => {
    if (!renameDocValue.trim()) { toast.error("Name cannot be empty"); return; }
    const { itemId, docKey } = renamingDoc;
    try {
      await axios.patch(`${API}/compliance/status/${itemId}/documents/${docKey}/rename`, { displayName: renameDocValue.trim() });
      setItemDocs(prev => ({
        ...prev,
        [itemId]: (prev[itemId] || []).map(d => {
          const key = d.id || d.fileName;
          return key === docKey ? { ...d, displayName: renameDocValue.trim() } : d;
        })
      }));
      toast.success("Document renamed");
      cancelRenameDoc();
    } catch {
      toast.error("Failed to rename document");
    }
  };

  // Handle paper copy toggle
  const [paperCopyModal, setPaperCopyModal] = useState({ open: false, itemId: null, itemName: '' });
  const [paperCopyDates, setPaperCopyDates] = useState({ effectiveDate: '', expirationDate: '' });
  const [paperCopyRecords, setPaperCopyRecords] = useState({}); // { itemId: [{ id, name, agency, policyNum, phone, effectiveDate, expirationDate }] }
  const [editingPaperRecord, setEditingPaperRecord] = useState(null); // { itemId, recordId, values }

  const handlePaperCopyToggle = async (itemId) => {
    const item = localItems.find(i => i.id === itemId);
    
    // If turning OFF, delete all records and toggle it
    if (item?.hasPaperCopy) {
      if (!window.confirm("Remove all paper copy records for this item?")) return;
      
      setTogglingPaperCopy(itemId);
      try {
        // Delete all records
        const records = paperCopyRecords[itemId] || [];
        await Promise.all(records.map(r => 
          axios.delete(`${API}/compliance/status/${itemId}/paper-copy-records/${r.id}`)
        ));
        
        const response = await axios.put(`${API}/compliance/status/${itemId}/paper-copy`);
        const updatedStatus = response.data;
        setLocalItems(prev => prev.map(item => 
          item.id === itemId 
            ? { ...item, hasPaperCopy: false, paperCopyDate: null, complianceStatus: updatedStatus.status }
            : item
        ));
        setPaperCopyRecords(prev => {
          const updated = { ...prev };
          delete updated[itemId];
          return updated;
        });
        toast.success("Paper copy removed");
        onUpdate?.();
      } catch (error) {
        toast.error("Failed to update");
      } finally {
        setTogglingPaperCopy(null);
      }
      return;
    }
    
    // If turning ON, just toggle and fetch existing records
    setTogglingPaperCopy(itemId);
    try {
      const response = await axios.put(`${API}/compliance/status/${itemId}/paper-copy`, {
        effectiveDate: null,
        expirationDate: null
      });
      const updatedStatus = response.data;
      setLocalItems(prev => prev.map(item => 
        item.id === itemId 
          ? { ...item, hasPaperCopy: true, paperCopyDate: updatedStatus.paperCopyDate, complianceStatus: 'missing' }
          : item
      ));
      // Fetch any existing records from DB
      await fetchPaperCopyRecords(itemId);
      toast.success("Paper copy enabled - add dates below");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to enable paper copy");
    } finally {
      setTogglingPaperCopy(null);
    }
  };

  const handleAddPaperRecord = (itemId) => {
    const newRecord = {
      id: `temp-${Date.now()}`,
      name: '',
      agency: '',
      policyNum: '',
      phone: '',
      effectiveDate: '',
      expirationDate: '',
      expirationNA: false
    };
    setEditingPaperRecord({ itemId, recordId: newRecord.id, values: newRecord });
  };

  const handleSavePaperRecord = async (itemId) => {
    const { recordId, values } = editingPaperRecord;
    
    if (!values.effectiveDate || (!values.expirationDate && !values.expirationNA)) {
      toast.error("Effective date and expiration date (or Never Expires) are required");
      return;
    }
    
    setTogglingPaperCopy(itemId);
    try {
      const response = await axios.post(`${API}/compliance/status/${itemId}/paper-copy-records`, {
        name: values.name,
        agency: values.agency,
        policyNum: values.policyNum,
        phone: values.phone,
        effectiveDate: values.effectiveDate,
        expirationDate: values.expirationDate,
        expirationNA: values.expirationNA || false
      });
      
      // Add to local state
      setPaperCopyRecords(prev => ({
        ...prev,
        [itemId]: [...(prev[itemId] || []), response.data]
      }));
      
      setEditingPaperRecord(null);
      toast.success("Paper copy record saved");
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save");
    } finally {
      setTogglingPaperCopy(null);
    }
  };

  const handleCancelPaperRecord = () => {
    setEditingPaperRecord(null);
  };

  const handleDeletePaperRecord = async (itemId, recordId) => {
    try {
      await axios.delete(`${API}/compliance/status/${itemId}/paper-copy-records/${recordId}`);
      setPaperCopyRecords(prev => ({
        ...prev,
        [itemId]: (prev[itemId] || []).filter(r => r.id !== recordId)
      }));
      
      // Check if that was the last record
      const remaining = (paperCopyRecords[itemId] || []).filter(r => r.id !== recordId);
      if (remaining.length === 0) {
        // Update local items to turn off toggle
        setLocalItems(prev => prev.map(item =>
          item.id === itemId ? { ...item, hasPaperCopy: false } : item
        ));
      }
      
      toast.success("Record deleted");
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to delete record");
    }
  };

  // Filter items
  let filteredItems = [...items];

  if (searchQuery) {
    const query = searchQuery.toLowerCase();
    filteredItems = filteredItems.filter(i => 
      i.name.toLowerCase().includes(query) ||
      i.description?.toLowerCase().includes(query)
    );
  }

  if (filterCategory !== "all") {
    filteredItems = filteredItems.filter(i => i.category === filterCategory);
  }

  if (!showDisabled) {
    filteredItems = filteredItems.filter(i => i.isEnabled !== false);
  }

  // Group ordering: Facility Documents first, Emergency Preparedness last,
  // so the checklist reads Facility → (others) → Emergency (labeled by category badge).
  const _catOrder = { facility_documents: 0, health_safety: 1, training: 2, emergency_preparedness: 9 };
  filteredItems = [...filteredItems].sort((a, b) => {
    const ca = _catOrder[a.category] ?? 5;
    const cb = _catOrder[b.category] ?? 5;
    if (ca !== cb) return ca - cb;
    return (a.order ?? 999) - (b.order ?? 999);
  });

  // Open create dialog
  const openCreateDialog = () => {
    setSelectedItem(null);
    setFormData({
      name: "",
      category: "facility_documents",
      description: "",
      expirationRule: "none",
      customExpirationDays: "",
      weight: 5,
      isEnabled: true,
      requiresDocument: true
    });
    setEditDialogOpen(true);
  };

  // Open edit dialog
  const openEditDialog = (item) => {
    setSelectedItem(item);
    setFormData({
      name: item.name,
      category: item.category,
      description: item.description || "",
      expirationRule: item.expirationRule || "none",
      customExpirationDays: item.customExpirationDays?.toString() || "",
      weight: item.weight || 5,
      isEnabled: item.isEnabled !== false,
      requiresDocument: item.requiresDocument !== false
    });
    setEditDialogOpen(true);
  };

  // Save item (create or update)
  const handleSave = async () => {
    if (!formData.name.trim()) {
      toast.error("Name is required");
      return;
    }

    setSaving(true);
    
    const payload = {
      name: formData.name.trim(),
      category: formData.category,
      description: formData.description.trim(),
      expirationRule: formData.expirationRule,
      customExpirationDays: formData.expirationRule === "custom" ? parseInt(formData.customExpirationDays) : null,
      weight: parseInt(formData.weight),
      isEnabled: formData.isEnabled,
      requiresDocument: formData.requiresDocument
    };

    try {
      if (selectedItem) {
        await axios.put(`${API}/compliance/items/${selectedItem.id}`, payload);
        toast.success("Item updated successfully");
      } else {
        await axios.post(`${API}/compliance/items`, payload);
        toast.success("Item created successfully");
      }
      setEditDialogOpen(false);
      onUpdate?.();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save item");
    } finally {
      setSaving(false);
    }
  };

  // Toggle item enabled status — with confirmation before disabling
  const toggleEnabled = async (item) => {
    const currentlyEnabled = item.isEnabled !== false;
    if (currentlyEnabled) {
      // Require confirmation before disabling
      if (!window.confirm(`Disable "${item.name}"?\n\nIt will be removed from your active checklist. You can restore it anytime by turning on "Show Disabled" and clicking Restore.`)) return;
    }
    try {
      const newEnabledState = !currentlyEnabled;
      await axios.put(`${API}/compliance/items/${item.id}`, { isEnabled: newEnabledState });
      toast.success(currentlyEnabled ? "Item disabled — turn on Show Disabled to restore it" : "Item enabled");
      fetchItems(showDisabled);
      onUpdate?.();
    } catch (error) {
      console.error("Toggle error:", error);
      toast.error("Failed to update item");
    }
  };

  // Delete item
  const handleDelete = async () => {
    if (!selectedItem) return;

    try {
      await axios.delete(`${API}/compliance/items/${selectedItem.id}/permanent`);
      toast.success("Item deleted permanently");
      setDeleteDialogOpen(false);
      onUpdate?.();
    } catch (error) {
      toast.error("Failed to delete item");
    }
  };

  // Reset to default compliance items (non-destructive - only adds missing)
  const handleResetToDefaults = async () => {
    setResetting(true);
    try {
      // Use add-missing-items instead of reset-defaults (non-destructive)
      const response = await axios.post(`${API}/compliance/add-missing-items`);
      const addedCount = response.data.added?.length || 0;
      if (addedCount > 0) {
        toast.success(`Added ${addedCount} missing compliance item(s)`);
      } else {
        toast.info("All default compliance items already exist");
      }
      setResetDialogOpen(false);
      // Refresh the items list
      fetchItems(showDisabled);
      onUpdate?.();
    } catch (error) {
      console.error("Reset error:", error);
      toast.error(error.response?.data?.detail || "Failed to add compliance items");
    } finally {
      setResetting(false);
    }
  };

  // Expiration rule labels
  const EXPIRATION_RULES = {
    none: "No Expiration",
    annual: "Annual (1 Year)",
    biennial: "Biennial (2 Years)",
    triennial: "Triennial (3 Years)",
    one_time: "One-Time",
    custom: "Custom Period"
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <Card>
        <CardHeader>
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Settings className="w-5 h-5" />
                Compliance Checklist Manager
              </CardTitle>
              <CardDescription>
                Add, edit, or disable compliance checklist items
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button onClick={openCreateDialog} className="gap-2">
                <Plus className="w-4 h-4" />
                Add Item
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                placeholder="Search items..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Category Filter */}
            <Select value={filterCategory} onValueChange={setFilterCategory}>
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Filter by category" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {Object.entries(categoryConfig).map(([key, config]) => (
                  <SelectItem key={key} value={key}>{config.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Show Disabled Toggle */}
            <div className="flex items-center gap-2">
              <Switch
                checked={showDisabled}
                onCheckedChange={setShowDisabled}
                id="show-disabled"
              />
              <Label htmlFor="show-disabled" className="text-sm">
                Show disabled
              </Label>
            </div>
            {/* Restore All button — only when disabled items are visible */}
            {showDisabled && localItems.some(i => i.isEnabled === false) && (
              <Button size="sm" variant="outline" onClick={handleRestoreAll}
                className="text-xs text-green-700 border-green-400 hover:bg-green-50">
                <RotateCcw className="w-3 h-3 mr-1" />
                Restore All Disabled
              </Button>
            )}
          </div>
          {/* Disable feature explanation */}
          <div className="mt-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5 text-xs text-amber-800">
            <strong>About the Disable feature:</strong> Use the <span className="inline-flex items-center gap-0.5 font-semibold">toggle icon</span> on any item to disable it. Disabled items are <strong>removed from your compliance score</strong> — use this only for requirements that genuinely do not apply to your center. Disabling an item you need will lower your score. To restore a disabled item, turn on <em>"Show disabled"</em> above and click <strong>Restore</strong>.
          </div>
        </CardContent>
      </Card>

      {/* Items List */}
      <div className="space-y-3">
        {filteredItems.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Archive className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">No items found matching your filters.</p>
            </CardContent>
          </Card>
        ) : (
          filteredItems.map((item, idx) => {
            const config = categoryConfig[item.category];
            const showSectionHeader = filterCategory === 'all' && !searchQuery &&
              (idx === 0 || filteredItems[idx - 1].category !== item.category);
            const sectionHeader = showSectionHeader ? (
              <div className="flex items-center gap-2 pt-5 pb-1 px-1" data-testid={`section-header-${item.category}`}>
                <div className={`h-6 w-1.5 rounded-full ${item.category === 'emergency_preparedness' ? 'bg-rose-500' : item.category === 'facility_documents' ? 'bg-blue-500' : item.category === 'children_records' ? 'bg-green-500' : item.category === 'staff_records' ? 'bg-purple-500' : 'bg-gray-400'}`} />
                <h3 className="text-sm font-bold uppercase tracking-wide text-[#1a365d]">{config?.label || 'Other'}</h3>
              </div>
            ) : null;
            const isExpanded = expandedItem === item.id;
            const isUploading = uploadingItem === item.id;
            const isTogglingPaper = togglingPaperCopy === item.id;
            const isFirstAidKit = (item.name || '').trim().toLowerCase() === 'first-aid kit';
            // Icon-only cosmetic grouping (no scoring/logic impact) — these items show the paper icon like First-Aid Kit
            const PAPER_ICON_ITEM_NAMES = ['staff data sheet', 'provider survey/facility information sheet', 'child care provider facility affirmation statements', 'certificate of compliance'];
            const usesPaperIcon = isFirstAidKit || PAPER_ICON_ITEM_NAMES.includes((item.name || '').trim().toLowerCase());
            // Facility items that should use a simple toggle + optional generic upload —
            // no insurance-style Provider/Policy #/Policy Phone # fields (Liability Insurance keeps those; First-Aid Kit stays upload-free)
            const SIMPLE_UPLOAD_ITEM_NAMES = ['certificate of compliance', 'occupancy permit', 'annual excursion/transportation letter', 'enrolled children list', 'how to identify & prevent shaken baby/abuse/head trauma', 'staff data sheet', 'provider survey/facility information sheet', 'child care provider facility affirmation statements', 'emergency continuity of operations plan (coop)'];
            const isSimpleUpload = SIMPLE_UPLOAD_ITEM_NAMES.includes((item.name || '').trim().toLowerCase());
            // Fire Drill: Paper Copy on File toggle alone marks it compliant — no
            // generic insurance-style "Add Paper Copy Record" card/fields for this item.
            const isFireDrill = (item.name || '').toLowerCase().includes('fire drill') || (item.name || '').toLowerCase().includes('fire detection');
            // Auto-computed items must rely solely on complianceStatus — not paper copy or generic docs
            const isAutoComputed = ['liability insurance', 'occupancy permit', 'emergency plan'].some(
              k => item.name.toLowerCase().includes(k)
            );
            // Data-driven comparison item — status is derived from real child records.
            // The manual "Paper Copy on File" toggle must NOT be able to mask a missing state.
            const isComparisonReport = getEmbeddedTabType(item.name) === 'child_service_report';
            // Emergency Drill Log is date-driven: completion requires a drill dated this
            // calendar year (or a dated paper copy). A bare upload/toggle must NOT fake it.
            const isDateDrivenDrill = getEmbeddedTabType(item.name) === 'emergency_drill';
            const isComplete = (isComparisonReport || isDateDrivenDrill)
              ? (item.complianceStatus === 'complete' || item.complianceStatus === 'current' || item.complianceStatus === 'expiring')
              : isAutoComputed
              ? (item.complianceStatus === 'complete' || item.complianceStatus === 'current' || item.complianceStatus === 'expiring')
              : isFirstAidKit
              ? !!item.hasPaperCopy
              : (item.complianceStatus === 'complete' || item.complianceStatus === 'current' || item.complianceStatus === 'expiring' || item.hasPaperCopy || item.hasUploadedDocument || (itemDocs[item.id]?.length > 0));

            // Emergency Contact List: render the table inside the standard card when expanded
            if (getEmbeddedTabType(item.name) === 'emergency_contact_list') {
              // Falls through to standard card rendering below — table shown via expanded section
            }

            // Paper Copy on File section — reused either standalone (non-drill) or
            // rendered inside the blue Emergency Drill Log card (drill).
            const paperCopySection = (!isFireDrill && !isComparisonReport && !item.name?.toLowerCase().includes('emergency contact list')) ? (
              <div className={isDateDrivenDrill ? "pb-6 border-b border-blue-200" : "mb-4 p-3 bg-white rounded-lg border"}>
                {isDateDrivenDrill && (
                  <p className="text-xs font-semibold text-blue-800 mb-2 uppercase tracking-wide">Part of Emergency Drill Log — Paper Copy option</p>
                )}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Paperclip className="w-4 h-4 text-gray-500" />
                    <span className="text-sm font-medium">{isFirstAidKit ? 'First-Aid Kit Complete' : 'Paper Copy on File'}</span>
                    {item.hasPaperCopy ? (
                      <span className="text-green-600 text-sm font-medium">✓ Received</span>
                    ) : (
                      <span className="text-gray-400 text-sm">Not marked</span>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    {isTogglingPaper && (
                      <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                    )}
                    <Switch
                      checked={item.hasPaperCopy || false}
                      onCheckedChange={() => handlePaperCopyToggle(item.id)}
                      disabled={isTogglingPaper}
                    />
                  </div>
                </div>
                {!item.hasPaperCopy && (
                  <p className="text-xs text-gray-400 mt-1 ml-6">Toggle on once this document is on file to mark this item compliant.</p>
                )}
                {!isFirstAidKit && !isSimpleUpload && item.hasPaperCopy && (
                  <div className="mt-2 ml-6 space-y-2">
                    {/* Saved paper copy records - displayed like uploaded documents */}
                    {(paperCopyRecords[item.id] || []).map(record => (
                      <div key={record.id} className="rounded-lg border px-4 py-3 bg-white border-gray-200">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            {/* Line 1: icon + name + details */}
                            <div className="flex items-center gap-2 flex-wrap">
                              <FileText className="w-4 h-4 flex-shrink-0 text-blue-700" />
                              <span className="text-sm font-semibold text-gray-900 truncate">{record.name || 'Paper Copy Document'}</span>
                              {record.agency && <span className="text-xs text-gray-500">· {record.agency}</span>}
                              {record.policyNum && <span className="text-xs text-gray-400">#{record.policyNum}</span>}
                              {record.phone && <span className="text-xs text-blue-600 font-medium">📞 {record.phone}</span>}
                            </div>
                            {/* Line 2: dates or Never Expires badge */}
                            {record.expirationNA ? (
                              <div className="flex items-center gap-2 mt-1 ml-6">
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-green-100 text-green-800">
                                  Never Expires
                                </span>
                              </div>
                            ) : (record.effectiveDate || record.expirationDate) && (
                              <div className="flex items-center gap-4 mt-1 ml-6 text-xs">
                                {record.effectiveDate && (
                                  <span className="text-gray-500">
                                    <span className="font-medium text-gray-600">Effective Date:</span> {new Date(record.effectiveDate).toLocaleDateString()}
                                  </span>
                                )}
                                {record.expirationDate && (
                                  <span className="text-gray-500">
                                    <span className="font-medium text-gray-600">Expiration Date:</span> {new Date(record.expirationDate).toLocaleDateString()}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1.5 flex-shrink-0">
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => handleDeletePaperRecord(item.id, record.id)}
                              className="h-7 w-7 p-0 text-red-500 border-red-200 hover:bg-red-50"
                              data-testid={`delete-paper-${record.id}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    
                    {/* Inline add/edit form - EXACT copy of LiabilityInsuranceTab pattern */}
                    {editingPaperRecord && editingPaperRecord.itemId === item.id ? (
                      <div className="space-y-2">
                        {/* Row 1: name, agency, policy, phone */}
                        <div className="flex items-center gap-2">
                          <Input
                            value={editingPaperRecord.values.name}
                            onChange={(e) => setEditingPaperRecord(prev => ({
                              ...prev,
                              values: { ...prev.values, name: e.target.value }
                            }))}
                            placeholder="Document name"
                            className="h-7 text-xs w-48"
                            autoFocus
                          />
                          {item.name?.toLowerCase().includes('insurance') && (
                            <>
                              <Input
                                value={editingPaperRecord.values.agency}
                                onChange={(e) => setEditingPaperRecord(prev => ({
                                  ...prev,
                                  values: { ...prev.values, agency: e.target.value }
                                }))}
                                placeholder="Provider"
                                className="h-7 text-xs w-32"
                              />
                              <Input
                                value={editingPaperRecord.values.policyNum}
                                onChange={(e) => setEditingPaperRecord(prev => ({
                                  ...prev,
                                  values: { ...prev.values, policyNum: e.target.value }
                                }))}
                                placeholder="Policy #"
                                className="h-7 text-xs w-28"
                              />
                              <Input
                                value={editingPaperRecord.values.phone}
                                onChange={(e) => setEditingPaperRecord(prev => ({
                                  ...prev,
                                  values: { ...prev.values, phone: e.target.value }
                                }))}
                                placeholder="Policy Phone #"
                                className="h-7 text-xs w-32"
                                type="tel"
                              />
                            </>
                          )}
                        </div>
                        {/* Row 2: dates + save/cancel */}
                        <div className="flex items-center gap-4">
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">Effective Date:</span>
                            <Input
                              type="date"
                              value={editingPaperRecord.values.effectiveDate}
                              onChange={(e) => setEditingPaperRecord(prev => ({
                                ...prev,
                                values: { ...prev.values, effectiveDate: e.target.value }
                              }))}
                              className="h-7 text-xs w-36"
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="text-xs font-medium text-gray-600 whitespace-nowrap">Expiration Date:</span>
                            <Input
                              type="date"
                              value={editingPaperRecord.values.expirationDate}
                              onChange={(e) => setEditingPaperRecord(prev => ({
                                ...prev,
                                values: { ...prev.values, expirationDate: e.target.value, expirationNA: false }
                              }))}
                              className="h-7 text-xs w-36"
                              disabled={editingPaperRecord.values.expirationNA}
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <Checkbox
                              checked={editingPaperRecord.values.expirationNA || false}
                              onCheckedChange={(checked) => setEditingPaperRecord(prev => ({
                                ...prev,
                                values: { ...prev.values, expirationNA: checked, expirationDate: checked ? "" : prev.values.expirationDate }
                              }))}
                              className="h-4 w-4"
                            />
                            <span className="text-xs text-gray-600 whitespace-nowrap">Never Expires</span>
                          </div>
                          <Button
                            size="sm"
                            onClick={() => handleSavePaperRecord(item.id)}
                            className="h-7 bg-green-600 hover:bg-green-700 text-white text-xs px-3"
                            data-testid={`save-paper-record-${item.id}`}
                          >
                            <Check className="w-3.5 h-3.5 mr-1" />
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={handleCancelPaperRecord}
                            title="Discard"
                            className="h-7 w-7 p-0 text-red-500 border-red-200 hover:bg-red-50"
                            data-testid={`discard-paper-record-${item.id}`}
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* Add button - always visible when not editing */
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleAddPaperRecord(item.id)}
                        className="text-blue-700 border-blue-300 hover:bg-blue-50 h-7 text-xs"
                        data-testid={`add-paper-copy-${item.id}`}
                      >
                        <Plus className="w-3.5 h-3.5 mr-1" />
                        Add Paper Copy Record
                      </Button>
                    )}
                  </div>
                )}
                {/* Drag & drop upload — directly under Paper Copy on File.
                    Skipped for simple-upload items that already have their own
                    Standard Upload section below (no embedded tab), to avoid two upload boxes for the same item. */}
                {((isSimpleUpload && getEmbeddedTabType(item.name)) || item.name?.toLowerCase().includes('emergency plan delivery') || item.name?.toLowerCase().includes('documentation of emergency plan') || item.name?.toLowerCase().includes('continuity of operations')) && (
                  <UploadDropZone itemId={item.id} onSuccess={() => { fetchItems(showDisabled); fetchItemDocs(item.id); onUpdate?.(); }} />
                )}
              </div>
            ) : null;


            return (
              <React.Fragment key={item.id}>
              {sectionHeader}
              <Card 
                className={`${!item.isEnabled ? 'opacity-60' : ''}`}
                data-testid={`manager-item-${item.id}`}
              >
                <CardContent className="py-4">
                  {/* Main Row */}
                  <div className="flex items-center gap-4">
                    {/* Status Indicator */}
                    <div className="flex-shrink-0">
                      {isComplete ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-500" />
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <h4 className="font-semibold text-gray-900">
                          {item.name === "Certificate of Compliance" ? "Certificate of Compliance License" : item.name}
                        </h4>
                        {isComplete && (
                          <Badge className="bg-green-100 text-green-700 text-xs">Received</Badge>
                        )}
                        {!item.isEnabled && (
                          <Badge variant="secondary" className="text-xs bg-red-100 text-red-700">Disabled</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                        <Badge variant="outline" className="text-xs">{config?.label}</Badge>
                        <Badge variant="outline" className="text-xs">Weight: {item.weight}</Badge>
                        {(item.name || '').trim().toLowerCase() === 'emergency continuity of operations plan (coop)' && item.isEnabled !== false && (
                          <span className="text-xs text-amber-600 whitespace-nowrap" data-testid="coop-na-hint">
                            Use the disable toggle to exclude it from your score →
                          </span>
                        )}
                        {item.hasPaperCopy && (
                          <Badge className="bg-blue-100 text-blue-700 text-xs">
                            <Paperclip className="w-3 h-3 mr-1" />
                            Paper Copy
                          </Badge>
                        )}
                        {item.hasUploadedDocument && (
                          <Badge className="bg-purple-100 text-purple-700 text-xs">
                            <FileText className="w-3 h-3 mr-1" />
                            {item.documentCount || 1} Doc{(item.documentCount || 1) > 1 ? 's' : ''}
                          </Badge>
                        )}
                      </div>
                      {isComparisonReport && !isComplete && (
                        <p className="text-xs font-medium text-red-600 mt-1.5" data-testid="csr-missing-subtitle">
                          {typeof item.comparisonMissing === 'number'
                            ? `${item.comparisonMissing} of ${item.comparisonTotal} full-time ${item.comparisonMissing === 1 ? 'child is' : 'children are'} missing a current service report`
                            : 'Some full-time children are missing a current service report'} — expand to see who.
                        </p>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {/* Restore button for disabled items - more prominent */}
                      {!item.isEnabled && (
                        <Button
                          size="sm"
                          variant="default"
                          onClick={() => handleRestoreItem(item)}
                          disabled={restoringItem === item.id}
                          className="bg-green-600 hover:bg-green-700 text-white gap-1"
                          title="Restore this item to the checklist"
                        >
                          {restoringItem === item.id ? (
                            <Loader2 className="w-4 h-4 animate-spin" />
                          ) : (
                            <RotateCcw className="w-4 h-4" />
                          )}
                          Restore
                        </Button>
                      )}
                      {/* Toggle Enable/Disable - Temporarily remove from checklist without deleting */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => toggleEnabled(item)}
                        title={item.isEnabled !== false ? "Disable — removes this item from your compliance score (only use if it does not apply to your center)" : "Enable — adds this item back to your compliance score"}
                      >
                        {item.isEnabled !== false ? (
                          <ToggleLeft className="w-5 h-5 text-gray-500" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 text-green-600" />
                        )}
                      </Button>
                      {/* Open/Expand for embedded tabs OR Upload for regular items */}
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          const newExpanded = isExpanded ? null : item.id;
                          setExpandedItem(newExpanded);
                          if (newExpanded) {
                            fetchItemDocs(newExpanded);
                            if (item.hasPaperCopy) fetchPaperCopyRecords(newExpanded);
                          }
                        }}
                        className="gap-1"
                        title={(getEmbeddedTabType(item.name) || usesPaperIcon) ? "Open form" : "Upload document or mark paper copy"}
                      >
                        {(getEmbeddedTabType(item.name) || usesPaperIcon) ? (
                          <>
                            <FileText className="w-4 h-4" />
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </>
                        ) : (
                          <>
                            <Upload className="w-4 h-4" />
                            {isExpanded ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
                          </>
                        )}
                      </Button>
                      {/* Edit - Always available to edit settings */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(item)}
                        title="Edit item settings"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      {/* Delete */}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setSelectedItem(item);
                          setDeleteDialogOpen(true);
                        }}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        title="Delete item permanently"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>

                  {/* Expanded Section - Upload & Paper Copy OR Embedded Tab */}
                  {isExpanded && (
                    <div className="mt-4 pt-4 border-t bg-gray-50 rounded-lg p-4">
                      {/* First-Aid Kit: static physical-inspection checklist (system item, not editable by customers) */}
                      {isFirstAidKit && (
                        <div className="mb-4 p-3 bg-white rounded-lg border" data-testid="first-aid-kit-checklist">
                          <p className="text-sm font-semibold text-gray-900">Verify each classroom First-Aid Kit contains:</p>
                          <ul className="mt-2 space-y-1 text-sm text-gray-700 list-disc pl-5">
                            <li>Soap</li>
                            <li>Assorted adhesive bandages</li>
                            <li>Sterile gauze pads</li>
                            <li>Tweezers</li>
                            <li>Tape</li>
                            <li>Scissors</li>
                            <li>Disposable nonporous gloves</li>
                          </ul>
                          <p className="mt-2 text-sm text-gray-700">
                            <span className="font-medium">Current Emergency Contact List</span> — inspector observation requirement.
                            <span className="text-gray-500"> Note: this is not one of the seven First-Aid Kit contents listed in §3270.75.</span>
                          </p>
                          <p className="mt-2 text-sm text-gray-700">
                            <span className="font-medium">Excursions:</span> One First-Aid Kit per child-care group must accompany the group and include a bottle of water.
                          </p>
                        </div>
                      )}
                      {/* Fire Drill & Fire Detection Log: dedicated, isolated Paper Copy on
                          File confirmation only. Intentionally does NOT share the generic
                          insurance-style paper-copy-record workflow below (no Add Paper Copy
                          Record card, no Document Name/Provider/Policy #/Policy Phone #/dates). */}
                      {isFireDrill && (
                        <div className="mb-4 p-3 bg-white rounded-lg border">
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Paperclip className="w-4 h-4 text-gray-500" />
                              <span className="text-sm font-medium">Paper Copy on File</span>
                              {item.hasPaperCopy ? (
                                <span className="text-green-600 text-sm font-medium">✓ Received</span>
                              ) : (
                                <span className="text-gray-400 text-sm">Not marked</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {isTogglingPaper && (
                                <Loader2 className="w-4 h-4 animate-spin text-gray-400" />
                              )}
                              <Switch
                                checked={item.hasPaperCopy || false}
                                onCheckedChange={() => handlePaperCopyToggle(item.id)}
                                disabled={isTogglingPaper}
                              />
                            </div>
                          </div>
                          {/* Fire Drill: optional generic upload — no insurance-style fields */}
                          {item.hasPaperCopy && (
                            <UploadDropZone itemId={item.id} onSuccess={() => { fetchItems(showDisabled); fetchItemDocs(item.id); onUpdate?.(); }} />
                          )}
                        </div>
                      )}
                      {/* Emergency Drill Log: frequency notice — rendered first, above the Paper Copy card */}
                      {isDateDrivenDrill && (
                        <div className="mb-4 flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3" data-testid="drill-frequency-note">
                          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                          <p className="text-xs text-amber-800">
                            <span className="font-semibold">Drill frequency varies by state.</span> Check your
                            state's licensing requirement for how often emergency drills are required
                            (e.g., Pennsylvania is once a year). Enter your own <span className="font-semibold">Expiration Date</span> below —
                            it does <span className="font-semibold">not</span> prepopulate. That date is what marks this item Compliant / Expiring / Expired.
                          </p>
                        </div>
                      )}
                      {/* Paper Copy on File — for the Emergency Drill Log it is rendered
                          inside the blue card (below); here only for non-drill items */}
                      {!isDateDrivenDrill && paperCopySection}

                      {/* Uploaded Documents List */}
                      {(() => {
                        const docs = itemDocs[item.id] || [];
                        if (docs.length === 0 && !loadingDocs) return null;
                        return (
                          <div className="mb-4 rounded-lg border border-purple-200 bg-purple-50 p-3">
                            <p className="text-sm font-semibold text-purple-800 mb-2 flex items-center gap-2">
                              <FileText className="w-4 h-4" />
                              Uploaded Documents ({docs.length})
                            </p>
                            {loadingDocs === item.id ? (
                              <div className="flex items-center gap-2 text-xs text-slate-400"><Loader2 className="w-3 h-3 animate-spin" /> Loading...</div>
                            ) : (
                              <div className="space-y-1.5">
                                {docs.map(doc => {
                                  const docKey = doc.id || doc.fileName;
                                  const isRenaming = renamingDoc !== null && renamingDoc.itemId === item.id && renamingDoc.docKey === docKey;
                                  const docName = doc.displayName || doc.fileName || "Uploaded Document";
                                  return (
                                    <div key={doc.id || doc.fileName} className="flex items-center justify-between rounded-lg bg-white border border-purple-100 px-3 py-2">                                      <div className="flex items-center gap-2 min-w-0 flex-1">
                                        <FileText className="w-4 h-4 text-purple-500 flex-shrink-0" />
                                        {isRenaming ? (
                                          <div className="flex items-center gap-1.5 flex-1">
                                            <input
                                              value={renameDocValue}
                                              onChange={e => setRenameDocValue(e.target.value)}
                                              onKeyDown={e => { if (e.key === 'Enter') saveRenameDoc(); if (e.key === 'Escape') cancelRenameDoc(); }}
                                              className="text-xs border border-purple-400 rounded px-2 py-1 w-48 focus:outline-none focus:ring-2 focus:ring-purple-400"
                                              autoFocus
                                            />
                                            <button onClick={saveRenameDoc} className="text-green-600 hover:text-green-700"><Check className="w-3.5 h-3.5" /></button>
                                            <button onClick={cancelRenameDoc} className="text-gray-400 hover:text-gray-600"><X className="w-3.5 h-3.5" /></button>
                                          </div>
                                        ) : (
                                          <div className="flex items-center gap-2 min-w-0 flex-1">
                                            <span className="text-xs font-medium text-slate-800 truncate">{docName}</span>
                                            <button
                                              onClick={() => startRenameDoc(item.id, doc)}
                                              className="text-purple-400 hover:text-purple-700 transition-colors flex-shrink-0"
                                              title="Rename document"
                                            >
                                              <Pencil className="w-3.5 h-3.5" />
                                            </button>
                                          </div>
                                        )}
                                      </div>
                                      <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                                        <Button size="sm" variant="outline" className="h-7 px-2 text-xs text-blue-600 border-blue-200 hover:bg-blue-50" onClick={() => viewDocument(item.id, doc.id || doc.fileName, doc.fileName)}>
                                          View
                                        </Button>
                                        <Button size="sm" variant="ghost" className="h-7 px-2 text-red-500 hover:bg-red-50" onClick={() => deleteDocument(item.id, doc)} data-testid={`delete-doc-${docKey}`}>
                                          <Trash2 className="w-3 h-3" />
                                        </Button>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {/* Check if this item has an embedded tab component */}
                      {getEmbeddedTabType(item.name) === 'liability_insurance' ? (
                        <div className="bg-white rounded-lg border">
                          <LiabilityInsuranceTab onComplianceChange={() => { fetchItems(showDisabled); onUpdate?.(); }} />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'occupancy_permit' ? (
                        <div className="bg-white rounded-lg border">
                          <OccupancyPermitTab onComplianceChange={() => { fetchItems(showDisabled); onUpdate?.(); }} />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'fire_drill' ? (
                        <div className="bg-white rounded-lg border">
                          <FireDrillLogTab />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'transportation' ? (
                        <div className="bg-white rounded-lg border">
                          <TransportationNotificationTab onComplianceChange={() => { fetchItems(showDisabled); onUpdate?.(); }} />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'emergency_drill' ? (
                        <div className="bg-white rounded-lg border">
                          <EmergencyDrillLogTab paperCopySection={paperCopySection} onUpload={() => { fetchItemDocs(item.id); fetchItems(showDisabled); onUpdate?.(); }} />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'emergency_plan_delivery' ? (
                        <div className="bg-white rounded-lg border">
                          <EmergencyPlanDeliveryTab onComplianceChange={() => { fetchItems(showDisabled); onUpdate?.(); }} />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'coop_plan' ? (
                        <div className="bg-white rounded-lg border">
                          <COOPTab />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'emergency_plan_procedure' ? (
                        <div className="bg-white rounded-lg border">
                          <EmergencyPlanProcedureTab />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'emergency_contact_list' ? (
                        <div className="bg-white rounded-lg border overflow-hidden">
                          <EmergencyContactReportPage embedded />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'enrolled_children' ? (
                        <div className="bg-white rounded-lg border">
                          <EnrolledChildrenListTab />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'shaken_baby_policy' ? (
                        <div className="bg-white rounded-lg border">
                          <ShakenBabyPolicyTab />
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'child_service_report' ? (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg border p-4">
                            <ChildServiceReportSummaryTab />
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-700 font-medium">If you upload your own document, toggle "Paper Copy on File" above to mark this item as received.</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Upload className="w-4 h-4" /> Upload Documents
                            </Label>
                            <input type="file" ref={(el) => fileInputRefs.current[item.id] = el} onChange={(e) => { Array.from(e.target.files || []).forEach(f => handleFileUpload(item.id, f)); e.target.value = ''; }} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" multiple />
                            <Button variant="outline" className="w-full" onClick={() => fileInputRefs.current[item.id]?.click()} disabled={isUploading}>
                              {isUploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4 mr-2" />Choose Files</>)}
                            </Button>
                            <div onDragOver={(e) => { e.preventDefault(); setDragOverItem(item.id); }} onDragLeave={() => setDragOverItem(null)} onDrop={(e) => { e.preventDefault(); setDragOverItem(null); Array.from(e.dataTransfer.files || []).forEach(f => handleFileUpload(item.id, f)); }} className={`rounded-xl border-2 border-dashed p-4 text-center transition-colors ${dragOverItem === item.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                              <p className="text-xs text-gray-400">{dragOverItem === item.id ? 'Drop files here' : 'Or drag & drop files here'}</p>
                            </div>
                            <p className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG (max 15MB each)</p>
                          </div>
                        </div>
                      ) : getEmbeddedTabType(item.name) === 'staff_evaluation' ? (
                        <div className="space-y-4">
                          <div className="bg-white rounded-lg border p-4">
                            <StaffEvaluationSummaryTab />
                          </div>
                          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                            <p className="text-xs text-blue-700 font-medium">If you upload your own document, toggle "Paper Copy on File" above to mark this item as received.</p>
                          </div>
                          <div className="space-y-2">
                            <Label className="text-sm font-medium flex items-center gap-2">
                              <Upload className="w-4 h-4" /> Upload Documents
                            </Label>
                            <input type="file" ref={(el) => fileInputRefs.current[item.id] = el} onChange={(e) => { Array.from(e.target.files || []).forEach(f => handleFileUpload(item.id, f)); e.target.value = ''; }} className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" multiple />
                            <Button variant="outline" className="w-full" onClick={() => fileInputRefs.current[item.id]?.click()} disabled={isUploading}>
                              {isUploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</>) : (<><Upload className="w-4 h-4 mr-2" />Choose Files</>)}
                            </Button>
                            <div onDragOver={(e) => { e.preventDefault(); setDragOverItem(item.id); }} onDragLeave={() => setDragOverItem(null)} onDrop={(e) => { e.preventDefault(); setDragOverItem(null); Array.from(e.dataTransfer.files || []).forEach(f => handleFileUpload(item.id, f)); }} className={`rounded-xl border-2 border-dashed p-4 text-center transition-colors ${dragOverItem === item.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'}`}>
                              <p className="text-xs text-gray-400">{dragOverItem === item.id ? 'Drop files here' : 'Or drag & drop files here'}</p>
                            </div>
                            <p className="text-xs text-gray-400">PDF, DOC, DOCX, JPG, PNG (max 15MB each)</p>
                          </div>
                        </div>
                      ) : isFirstAidKit ? null : (
                        /* Standard Upload with added Drag & Drop */
                        <div className="space-y-2">
                          <Label className="text-sm font-medium flex items-center gap-2">
                            <Upload className="w-4 h-4" />
                            Upload Document
                          </Label>
                          <p className="text-xs text-gray-500">
                            Upload a digital copy to mark as received
                          </p>
                          <input
                            type="file"
                            ref={(el) => fileInputRefs.current[item.id] = el}
                            onChange={(e) => handleFileUpload(item.id, e.target.files?.[0])}
                            className="hidden"
                            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                          />
                          <Button
                            variant="outline"
                            className="w-full"
                            onClick={() => fileInputRefs.current[item.id]?.click()}
                            disabled={isUploading}
                          >
                            {isUploading ? (
                              <>
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                Uploading...
                              </>
                            ) : (
                              <>
                                <Upload className="w-4 h-4 mr-2" />
                                Choose File
                              </>
                            )}
                          </Button>
                          {/* Drag & Drop Zone */}
                          <div
                            onDragOver={(e) => { e.preventDefault(); setDragOverItem(item.id); }}
                            onDragLeave={() => setDragOverItem(null)}
                            onDrop={(e) => {
                              e.preventDefault();
                              setDragOverItem(null);
                              const file = e.dataTransfer.files?.[0];
                              if (file) handleFileUpload(item.id, file);
                            }}
                            className={`rounded-xl border-2 border-dashed p-4 text-center transition-colors ${
                              dragOverItem === item.id
                                ? 'border-blue-500 bg-blue-50'
                                : 'border-gray-200 bg-gray-50'
                            }`}
                          >
                            <p className="text-xs text-gray-400">
                              {dragOverItem === item.id ? 'Drop file here' : 'Or drag & drop a file here'}
                            </p>
                          </div>
                          <p className="text-xs text-gray-400">
                            PDF, DOC, DOCX, JPG, PNG (max 15MB)
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
              </React.Fragment>
            );
          })
        )}
      </div>

      {/* Edit/Create Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedItem ? "Edit Compliance Item" : "Add Compliance Item"}
            </DialogTitle>
            <DialogDescription>
              {selectedItem ? "Update the compliance item details" : "Create a new compliance checklist item"}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label htmlFor="item-name">Name *</Label>
              <Input
                id="item-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="e.g., Liability Insurance Certificate"
                className="mt-1"
                disabled={(selectedItem?.name || '').trim().toLowerCase() === 'first-aid kit'}
              />
            </div>

            <div>
              <Label>Category *</Label>
              <Select
                value={formData.category}
                onValueChange={(val) => setFormData({ ...formData, category: val })}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(categoryConfig).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label htmlFor="item-description">Description</Label>
              <Textarea
                id="item-description"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                placeholder="Describe this compliance requirement..."
                className="mt-1"
                disabled={(selectedItem?.name || '').trim().toLowerCase() === 'first-aid kit'}
              />
              {(selectedItem?.name || '').trim().toLowerCase() === 'first-aid kit' && (
                <p className="mt-1 text-xs text-gray-500">The required First-Aid Kit contents and regulatory instructions are set by the system and cannot be edited. You can still adjust administrative settings like Weight.</p>
              )}
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Expiration Rule</Label>
                <Select
                  value={formData.expirationRule}
                  onValueChange={(val) => setFormData({ ...formData, expirationRule: val })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(EXPIRATION_RULES).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="item-weight">Weight (1-10)</Label>
                <Input
                  id="item-weight"
                  type="number"
                  min="1"
                  max="10"
                  value={formData.weight}
                  onChange={(e) => setFormData({ ...formData, weight: e.target.value })}
                  className="mt-1"
                />
              </div>
            </div>

            {formData.expirationRule === "custom" && (
              <div>
                <Label htmlFor="custom-days">Custom Expiration (Days)</Label>
                <Input
                  id="custom-days"
                  type="number"
                  min="1"
                  value={formData.customExpirationDays}
                  onChange={(e) => setFormData({ ...formData, customExpirationDays: e.target.value })}
                  placeholder="e.g., 180"
                  className="mt-1"
                />
              </div>
            )}

            {(selectedItem?.name || '').trim().toLowerCase() !== 'first-aid kit' && (
            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="requires-doc">Requires Document Upload</Label>
                <p className="text-xs text-muted-foreground">
                  Whether this item needs a document to be complete
                </p>
              </div>
              <Switch
                id="requires-doc"
                checked={formData.requiresDocument}
                onCheckedChange={(val) => setFormData({ ...formData, requiresDocument: val })}
              />
            </div>
            )}

            <div className="flex items-center justify-between py-2">
              <div>
                <Label htmlFor="is-enabled">Enabled</Label>
                <p className="text-xs text-muted-foreground">
                  Disabled items won't appear in the compliance dashboard
                </p>
              </div>
              <Switch
                id="is-enabled"
                checked={formData.isEnabled}
                onCheckedChange={(val) => setFormData({ ...formData, isEnabled: val })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving}>
              {saving ? "Saving..." : selectedItem ? "Save Changes" : "Create Item"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-red-600">⚠️ Are You Sure?</AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                You are about to permanently delete "<strong>{selectedItem?.name}</strong>".
              </p>
              <p>
                This will remove all associated status records and uploaded documents.
              </p>
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 mt-2">
                <p className="text-red-700 font-semibold text-sm">
                  ⚠️ WARNING: This form cannot be restored once deleted.
                </p>
                <p className="text-red-600 text-sm mt-1">
                  If you only need to temporarily remove this item from the compliance checklist, use the <strong>Disable</strong> button instead.
                </p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
            >
              Yes, Delete Permanently
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Add Missing Items Confirmation Dialog */}
      <AlertDialog open={resetDialogOpen} onOpenChange={setResetDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2 text-orange-600">
              <RefreshCw className="w-5 h-5" />
              Add Missing Compliance Items?
            </AlertDialogTitle>
            <AlertDialogDescription className="space-y-3">
              <p>
                This will add any missing <strong>default compliance items</strong> required for childcare licensing.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mt-2">
                <p className="text-green-700 font-semibold text-sm">
                  What happens:
                </p>
                <ul className="text-green-600 text-sm mt-1 list-disc list-inside space-y-1">
                  <li>Missing default items will be added</li>
                  <li>Existing items will NOT be modified</li>
                  <li>Your uploaded documents are preserved</li>
                  <li>Disabled items remain disabled</li>
                </ul>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={resetting}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleResetToDefaults}
              disabled={resetting}
              className="bg-green-600 hover:bg-green-700"
            >
              {resetting ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Adding...
                </>
              ) : (
                "Yes, Add Missing Items"
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
};

export default ComplianceChecklistManager;
